<template>
	<section class="my-reservations" data-testid="my-reservations-component">
		<div class="panel-wrapper hidden-print">
			<div class="container">
				<div class="page-head page-head-compact">
					<button class="btn btn-primary btn-xs" data-toggle="collapse" data-target="#myReservationsFiltersSub" aria-controls="myReservationsFiltersSub" data-label-collapsed="Filters" data-label-expanded="Hide Filters" aria-expanded="false">Filters</button>
				</div>
			</div>
		</div>

		<!-- Filters -->
		<SearchFilters
			v-if="facetData"
			filterPage="myReservations"
			id="myReservationsFiltersSub"
			ref="myReservationsFiltersSub"
			:facets="facetData"
			:filterTypes="filterTypes"
			:queryFilters="queryFilters"
			@rebuildQueryFilters="handleRebuildQueryFilters"
			@changed="handleFiltersChanged"
			prefix="ltf-my-reservations-filter-"
			class="collapse"
			:itemCssClass="itemCssClass"
			:savedSearchFilters="[]"
		/>

		<!-- Chiclets -->
		<Chiclets
			v-if="userKnown || userAuthenticated"
			:selected="searchChiclets"
			:savedSearchFilters="[]"
			:allowFavorites="false"
			@removed="handleRemovedChiclet"
			@clearAllChiclets="handleClearAllChiclets"
		/>

		<!-- ReservationList-->
		<div class="reservations-container">
			<div class="container">
				<div class="row">
					<div class="container reservation-list-container" role="region" aria-live="polite">
						<ul class="list-unstyled reservation-list">
							<li v-for="res in reservations" :key="res.id" data-testid="reservationItem">
								<div class="row">
									<div class="col-xs-12 col-sm-6 col-md-8 col-lg-10">
										<div class="row">
											<div class="col-xs-12 col-md-5 col-lg-4 col-xl-5">
												<div class="row">
													<div class="col-xs-12 col-xl-5">
														<p><TemporalDisplay :start="res.startMoment" :end="getEndDate(res)" template="date"/></p>
													</div>
													<div class="col-xs-12 col-xl-7">
														<h2 class="h5 reservation-name">{{ res.eventName }}</h2>
														<p class="card-instructor" v-if="res.instructors && res.instructors.length">Led by
															<span v-for="(instructor, index) in res.instructors" :key="index">
																<span v-if="instructor.subFor"><s>{{ instructor.subFor }}</s> </span>
																<span :class="{'card-instructor-sub': instructor.subFor}">{{ instructor.name }}</span>{{ index == res.instructors.length - 1 ? '' : (res.instructors.length === 2) ? ' and ' : (index === res.instructors.length - 2) ? ', and ' : ', ' }}
															</span>
														</p>
													</div>
												</div>
											</div>
											<div class="col-xs-12 col-md-7 col-lg-8 col-xl-7">
												<div class="row">
													<div class="col-xs-12 col-lg-7 col-xl-6">
														<div v-if="res.groupingDetail">
															<div v-for="group in res.groupingDetail" :key="group.label">
																<p><b>{{ group.label }}</b>: {{ group.details }}</p>
															</div>
															<div v-if="res.cancelByDate && isCancellationWindowOpen(res)" data-testid="cancelByDate">
																<p><b>Cancel By</b>: {{formatCancelByDate(res.cancelByDate)}}</p>
															</div>
														</div>
														<div v-else>
															<p class="reservation-time"><TemporalDisplay :start="res.startMoment" :end="res.endMoment" template="time"/></p>
														</div>
													</div>
													<div class="col-xs-12 col-lg-5 col-xl-6">
														<p>
															<span v-if="res.locationArray">{{ res.locationArray[0] }}<span v-if="res.locationArray[1]">,<br class="hidden-sm-down">{{ res.locationArray[1] }}</span><span v-if="res.locationArray[2]">,<br class="hidden-sm-down">{{ res.locationArray[2] }}</span></span>
														</p>
														<p><span :data-testid="`member_${res.id}`">Reserved for {{ res.memberName }}<br></span></p>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div class="col-xs-12 col-sm-6 col-md-4 col-lg-2 cta-column">
										<div class="btn-wrap">
											<button class="btn btn-sm btn-block btn-primary" v-if="!isMobileApp" @click="addToCalendar(res)">Add to Calendar</button>
											<button class="btn btn-sm btn-block btn-primary-outline" v-if="res.editCta && res.editCta.method === 'online'" data-testid="editBtn" @click="ctaClicked(res.editCta)">{{ buildCtaButtonText(res.editCta) }}</button>
										</div>
										<button class="btn btn-sm btn-block btn-link" v-if="res.cancelCta && res.cancelCta.method === 'online'" @click="ctaClicked(res.cancelCta)" data-testid="cancelBtn">{{ buildCtaButtonText(res.cancelCta) }}</button>
										<p class="cancel-message" v-if="res.cancelCta && res.cancelCta.method === 'phone'">
											<span :data-testid="`cancelCtaText_${res.id}`" v-html="buildPhoneCta(res.cancelCta, res.clubPhone)"></span>
											<span class="cancel-message-toggletip"><span v-if="isCamp(res.category)" data-toggletip data-char="i" data-position="bottom" data-btn-label="Camp Cancel Window Closed info">The modify or cancellation window is closed for this camp. If you have questions or concerns, please speak to the Kids Manager at the club.</span></span>
										</p>
									</div>
								</div>
							</li>

							<!-- Unauthenticated -->
							<li class="is-empty" v-if="!(userKnown || userAuthenticated) && !isWaiting">
								<p><a :href="loginNav">Log in to view your upcoming reservations.</a></p>
							</li>

							<!-- No reservations -->
							<li class="is-empty" v-else-if="(userKnown || userAuthenticated) && !reservations.length && !isWaiting">
								<p>
									There are no reservations that match the current criteria.
									<span v-if="isSingleMembership">Please select a different <span v-if="!isWorkExperience">reservation type or </span>date range.</span>
									<span v-if="!isSingleMembership">Please select a different member<span v-if="!isWorkExperience">, reservation type</span> or date range.</span>
								</p>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
		<!-- Loading Spinner -->
		<div role="alert" aria-live="assertive">
			<transition name="fade" appear>
				<div class="spinner-overlay" data-testid="spinner" v-show="isWaiting">
					<p class="sr-only">Classes are loading…</p>
					<LoadingSpinner />
				</div>
			</transition>
		</div>

		<Modal ref="cancelModal" ariaLabelled="myReservationCancelModalTitle">
			<template #title>{{cancelTitle}}</template>
			<template #default>
				<p v-if="cancelErrorMessage" data-testid="errorMessage" v-html="cancelErrorMessage"></p>
				<div data-testid="cancelMessage" v-html="cancelMessage"></div>
			</template>
			<template #footer>
				<button type="button" class="btn btn-link" data-testid="noBtn" @click="cancelModalCta(false)">{{ cancelDeclineButtonText }}</button>
				<button type="button" class="btn btn-primary" data-testid="yesBtn" @click="cancelModalCta(true)">{{ cancelConfirmButtonText }}</button>
			</template>
		</Modal>

		<Modal ref="editModal" ariaLabelled="myReservationEditModalTitle">
			<template #title>{{editTitle}}</template>
			<template #default>
				<div data-testid="editMessage" v-html="editMessage"></div>
			</template>
			<template #footer>
				<button type="button" class="btn btn-link" data-testid="addDaysBtn" @click="editModalCta(false)">{{editLinkButtonText}}</button>
				<button type="button" class="btn btn-primary" data-testid="editCampBtn" @click="editModalCta(true)">{{editConfirmButtonText}}</button>
			</template>
		</Modal>

		<AddToCalendar ref="addToCalendarComponent" />

	</section>
</template>

<script lang="ts">
import Vue, { defineComponent } from 'vue';
import _ from 'lodash';
import axios from 'axios';
import { baseComponentMixin } from '@/mixins/baseComponent.mixin';
import LoadingSpinner from '@/components/schreg-common/LoadingSpinner.vue';
import moment from 'moment';
import { StateService } from '@/services/state.service';
import TemporalDisplay from '@/components/schreg-common/TemporalDisplay.vue';
import AddToCalendar from '@/components/schreg-common/AddToCalendar.vue';
import { FrameworkService } from '@/services/framework.service';
import { AnalyticsService } from '@/services/analytics.service';
import Chiclets from '@/components/schreg-common/Chiclets.vue';
import { FilterTypes } from '@/services/filtering.service.config';
import SearchFilters from '@/components/schreg-common/SearchFilters.vue';
import Modal from '@/components/schreg-common/Modal.vue';
import { NotificationService } from '@/services/notification.service';
import { ClockService } from '@/services/clock.service';
import {type Chiclet, type Cta, type FacetData, type Reservation, ReservationCategory,
	type ReservationsFilters, UxSchedulesApi } from '@/apis/uxSchedules.api';
import { FilteringService, FilteringServicePages } from '@/services/filtering.service';
import { Helpers } from '@/common/helpers';
import type {ltFramework} from '@life-time-inc/enterprise-framework-core';
import {keys, ldClient} from "@/services/launchDarkly.service";

interface memberResult {
	name: string,
	value: string
}

export default defineComponent({
	name: 'MyReservationsComponent',
	config: {
		key: 'schreg.myReservations',
		devPath: 'my-reservations',
		exported: true
	},
	components: {
		SearchFilters,
		AddToCalendar,
		LoadingSpinner,
		TemporalDisplay,
		Modal,
		Chiclets
	},
	mixins: [
		baseComponentMixin
	],
	data() {
		return {
			reservations: [] as Reservation[],
			loginNav: '',
			isSingleMembership: false,
			isWorkExperience: false,
			facetData: null as FacetData[] | null,
			filterTypes: [] as string[], // TODO: Use FilterTypes as enum
			modalContext: null as Cta | null,
			searchFilters: {} as ReservationsFilters,
			queryFilters: [] as any[], // TODO: Find type for this
			members: [],
			searchChiclets: [] as Chiclet[],
			itemCssClass: '',
			cancelErrorMessage: '',
			cancelMessage: '',
			cancelTitle: '',
			cancelDeclineButtonText: 'No',
			cancelConfirmButtonText: 'Yes',
			editTitle: '',
			editLinkButtonText: '',
			editConfirmButtonText: '',
			editMessage: '',
			isMobileApp: false
		};
	},
	mounted() {
		this.isWaiting = true;
		this.getFrameworkData(this.completeMounting, ['ProfileStore', 'UrlStore']);

		// Override overflow: hidden on member site to prevent multiselects getting cut off at footer
		if (document.getElementsByClassName('main-mylt').length > 0) {
			const mainDiv = document.querySelector<HTMLElement>('.main-mylt');
			if (mainDiv) mainDiv.style.overflow = 'visible';
		}
	},
	methods: {
		completeMounting() {
			this.isWorkExperience = FrameworkService.isWorkExperience();
			this.isMobileApp = Helpers.isMobileApp();
			this.members = StateService.getAllMemberIdArray();
			this.isSingleMembership = this.members && this.members.length === 1;

			this.itemCssClass = this.isSingleMembership || this.isWorkExperience ? 'col-xs-12 col-md-6 col-lg-4' : 'col-xs-12 col-sm-6 col-lg-3';

			this.filterTypes = [
				FilterTypes.startDate,
				FilterTypes.endDate
			];

			if (!this.isSingleMembership) {
				this.filterTypes.push(FilterTypes.member);
			}

			if (!this.isWorkExperience) {
				this.filterTypes.push(FilterTypes.reservationType);
			}

			this.queryFilters = FilteringService.getSelectedFiltersFromQuery(this.filterTypes);
			this.searchFilters = FilteringService.getInitialFilters(FilteringServicePages.myReservations, this.queryFilters, this.filterTypes);
			this.searchChiclets = this.searchFilters.chiclets;
			this.loadMyReservations();
		},

		async loadMyReservations() {
			if (this.members && this.members.length > 0 && (this.userKnown || this.userAuthenticated)) {
				try {
					const response = await UxSchedulesApi.getMyReservations(this.searchFilters, 'ux-myReservations-get-v2');

					if (!response.ok) {
						return this.mountServerError(true, response.data.error);
					}

					if (!(this.userKnown || this.userAuthenticated)) {
						NotificationService.info(
							`Please <a href="${FrameworkService.globalUrl('loginResource')}">log in</a> to enable editing and canceling online.`
						).trigger();
					}

					let _reservations = [] as Reservation[];

					_reservations = response.data.results;
					_reservations.forEach(function(result) {
						result.locationArray = result.location?.split(',');
						result.startMoment = moment.parseZone(result.start);
						result.endMoment = moment.parseZone(result.end);
						const registrationData = result.registration;
						if (registrationData) {
							result.clubPhone = registrationData.clubPhone;

							if (!registrationData.registeredMembers || !registrationData.registeredMembers.length) {
								return;
							}

							const editCtas = _.find(registrationData.registeredMembers, m => m.id === result.memberId)?.editCtas;
							if (editCtas && editCtas.length > 0) {
								result.editCta = editCtas[0];
							}

							const cancelCtas = _.find(registrationData.registeredMembers, m => m.id === result.memberId)?.cancelCtas;
							if (cancelCtas && cancelCtas.length > 0) {
								result.cancelCta = cancelCtas[0];
							}
						}
					});

					const profileMembers = FrameworkService.data('profile.members') as ltFramework.IMember[];
					let sameMemberData = profileMembers.filter(v =>
						profileMembers.filter(v1 => v1.firstName === v.firstName).length > 1);
					let memberResult: memberResult[] = [];
					profileMembers.map(function(o) {
						if (sameMemberData.length > 1) {
							sameMemberData.map(function(p) {
								if (p.firstName === o.firstName) {
									return memberResult.push({
										name: o.firstName + ' ' + o.memberType,
										value: 'member:' + o.memberId
									});
								} else {
									return memberResult.push({
										name: o.firstName,
										value: 'member:' + o.memberId
									});
								}
							});
						} else {
							return memberResult.push({
								name: o.firstName,
								value: 'member:' + o.memberId
							});
						}
					});

					let memberNames =_.map(
						_.uniq(
							_.map(memberResult, function(o) {
								return JSON.stringify(o);
							})
						), function(o) {
							return JSON.parse(o);
						}
					);

					let filterData = [
						{
							filter: 'member',
							values: memberNames
						},
						{
							filter: 'reservationType',
							values: response.data.filters[0].values
						}
					];
					this.reservations = _reservations;
					this.facetData = filterData;
					this.isWaiting = false;

					// Listen for collapse events to update query string, etc. - here so that MyReservationsFiltersSub has mounted (due to getting facetData)
					this.$nextTick(() => {
						$('#myReservationsFiltersSub').on('show.bs.collapse', this.handleFiltersShow);
						$('#myReservationsFiltersSub').on('hide.bs.collapse', this.handleFiltersHide);
						$('[data-toggletip]').myltToggletip();
					});
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error('Error loading reservations:', error);
					this.mountServerError(true, 'An error occurred while fetching reservations.');
				}
			} else {
				this.loginNav = FrameworkService.globalUrl('loginResource');
				this.isWaiting = false;
			}
		},

		handleFiltersShow() {
			StateService.setQueryStringKey('showFilters', true);
		},

		handleFiltersHide() {
			StateService.setQueryStringKey('showFilters', false); // Set to false instead of removing, since it defaults to showing
		},

		handleRebuildQueryFilters() {
			// Validate the filters passed in the query string
			const invalid = (this.$refs.myReservationsFiltersSub as InstanceType<typeof SearchFilters>).validateQueryValues(this.queryFilters);
			if (invalid.length > 0) {
				const chics = [];
				for (const chic of this.searchChiclets) {
					// add valid filters from the query string to the chiclet bar
					if (!invalid.includes(chic.value)) {
						chics.push(chic);
					}
				}
				this.searchChiclets = chics;
			}
			this.queryFilters = [];

			// remove everything from the query string
			for (const filterType of this.filterTypes) {
				StateService.removeQueryStringKey(filterType);
			}

			// rebuild the query string
			this.$nextTick(() => {
				const query = (this.$refs.myReservationsFiltersSub as InstanceType<typeof SearchFilters>).getQueryStringValues();
				for (const item of query) {
					StateService.setQueryStringKey(item.name, item.values);
				}
			});

			// Show filters if the query string value is true, or if there is no query string value (default to "show")
			if (StateService.getQuery('showFilters') !== 'false') {
				$('#myReservationsFiltersSub').collapse('show');
			}
		},

		showCancelModal() {
			(this.$refs.cancelModal as InstanceType<typeof Modal>).show();
		},

		closeCancelModal() {
			(this.$refs.cancelModal as InstanceType<typeof Modal>).hide();
		},

		showEditModal() {
			(this.$refs.editModal as InstanceType<typeof Modal>).show();
		},

		closeEditModal() {
			(this.$refs.editModal as InstanceType<typeof Modal>).hide();
		},

		cancelModalCta(doCancel: boolean) {
			this.closeCancelModal();

			if (doCancel && this.modalContext) {
				const registrationId = this.modalContext.registrationId;
				const url = this.modalContext.url;
				const reservation = this.reservations.find(r => r.id === registrationId);
				const cancellationFailedMessage = reservation?.category === ReservationCategory.Camp
					? `We can't process your cancellation at this time. If you have questions or concerns, please speak to the Kids Manager at the club.`
					: 'Failed to cancel reservation. Please try again.';

				this.modalContext = null;
				if (registrationId) {
					this.isWaiting = true;
					let request = {
						method: 'delete',
						url: url,
						headers: {
							'X-LTF-SSOID': StateService.getSSOToken(),
							'ocp-apim-subscription-key': StateService.getApimKey()
						}
					};

					axios.request(request)
						.then(response => {
							// lifespa service will return a 200 status on a successful delete, sys-registrations will return a 204 status
							if (response.status !== 204 && response.status !== 200) {
								NotificationService.danger(cancellationFailedMessage).trigger();
								this.isWaiting = false;
							}

							// "Reservation Canceled" Analytics
							let reservationCanceled = _.find(this.reservations, ['id', registrationId]);
							if (reservationCanceled) {
								AnalyticsService.reservationCanceledAnalytics(reservationCanceled.eventName, reservationCanceled.upc, reservationCanceled.locationName, reservationCanceled.clubId);
							}

							// Pause an extra 2 seconds to give the indexes time to catch up
							ClockService.pause(2000).then(() => {
								this.completeMounting();
							});
						}, () => {
							this.isWaiting = false;
							NotificationService.danger(cancellationFailedMessage).trigger();
						});
				}
			}
		},

		editModalCta(doEdit: boolean) {
			this.closeEditModal();
			if (this.modalContext && (this.editLinkButtonText !== 'Cancel' || doEdit)) {
				const shouldUseMainUrl = this.editLinkButtonText === 'Cancel' ? doEdit : !doEdit;
				const url = FrameworkService.addCancelUrl(shouldUseMainUrl ? this.modalContext.url : this.modalContext.secondaryUrl);
				window.location.href = url;
			}
		},

		ctaClicked(cta?: Cta) {
			if (cta && cta.type === 'cancel') {
				// Cancel
				this.modalContext = cta;
				this.cancelErrorMessage = '';
				this.cancelTitle = 'Cancel Confirmation';
				this.cancelMessage = '<p>Are you sure you want to cancel?</p>';
				const reservation = this.reservations.find(r => r.id === cta.registrationId);
				if (reservation && cta.url) {
					if (reservation.category === ReservationCategory.Camp) {
						if (cta.isExternal) {
							cta.url = FrameworkService.addCancelUrl(cta.url);
							window.location.href = cta.url;
						} else {
							if (this.modalContext) {
								this.modalContext.url = cta.url;
							}
							this.setupCampCancelModal(reservation);
							this.showCancelModal();
						}
					} else {
						if (reservation.isProgressiveClass) {
							this.cancelMessage = `<p>Canceling will remove your child from the entire <b>${reservation.eventName}</b> class series.</p><p>If you are unable to attend just one day of the class, please contact the Kids Leader at the club at <a href="mailto:${reservation.clubEmail}">${reservation.clubEmail}</a>.</p><p>Are you sure you want to cancel?</p>`;
						}

						const displayRules = _.filter(reservation.registration.rules, r => r.displayOnFrontEnd !== undefined && r.displayOnFrontEnd);

						if (displayRules.length > 0) {
							const rule = displayRules[0];
							if (rule.errorMessage) {
								this.cancelErrorMessage = rule.errorMessage;
							}
						}

						this.showCancelModal();
					}
				}
			} else if (cta && cta.url) {
				// Edit/Modify
				if (cta.type === 'edit') {
					const reservation = this.reservations.find(r => r.id === cta.registrationId);
					if (reservation && reservation.category === ReservationCategory.Camp && (cta.secondaryUrl || !this.isCancellationWindowOpen(reservation)))
					{
						this.modalContext = cta;
						ldClient.waitForInitialization().then(async() => {
							const kidsCampFeatureFlag = await ldClient.variation(keys.kidsCampCancellation, false);
							this.setupCampEditModal(reservation, kidsCampFeatureFlag);
							//for now we don't want to show the edit modal if we don't have a secondaryUrl and are in the scenario where we would route to formstack
							if (cta.secondaryUrl || (!this.isCancellationWindowOpen(reservation) && kidsCampFeatureFlag)) {
								this.showEditModal();
								return;
							} else {
								cta.url = FrameworkService.addCancelUrl(cta.url);
								window.location.href = cta.url;
							}
						});
					} else {
						cta.url = FrameworkService.addCancelUrl(cta.url);
						window.location.href = cta.url;
					}
				} else {
					cta.url = FrameworkService.addCancelUrl(cta.url);
					window.location.href = cta.url;
				}
			}
		},

		setupCampCancelModal(reservation: Reservation) {
			ldClient.waitForInitialization().then(async() => {
				const kidsCampFeatureFlag = await ldClient.variation(keys.kidsCampCancellation, false);

				if(this.isCancellationWindowOpen(reservation) || !kidsCampFeatureFlag) {
					this.cancelTitle = 'Cancel Confirmation';
					this.cancelDeclineButtonText = 'No';
					this.cancelConfirmButtonText = 'Yes';
					this.cancelMessage = `<p>Are you sure you want to cancel the entire <b>${reservation.eventName}</b> reservation for <b>${reservation.memberName}</b> from <b>${this.formatCampRange(reservation.startMoment, reservation.endMoment)}</b>?</p>`;
				} else {
					this.cancelTitle = 'Are you sure?';
					this.cancelDeclineButtonText = 'No, Go Back';
					this.cancelConfirmButtonText = 'Yes, Continue';
					this.cancelMessage = `<p>Please note, with the camp start date approaching, canceling this camp is non-refundable. If you have a payment plan, you will still be automatically charged the cost of the camp.<br/><br/>Are you sure you want to cancel the entire <b>${reservation.eventName}</b> reservation for <b>${reservation.memberName}</b> from <b>${this.formatCampRange(reservation.startMoment, reservation.endMoment)}</b>?</p>`;
				}
			});
		},

		setupCampEditModal(reservation: Reservation, kidsCampFeatureFlag: boolean) {
				if(!this.isCancellationWindowOpen(reservation) && kidsCampFeatureFlag) {
					this.editTitle = 'Edit Camp';
					this.editLinkButtonText = 'Cancel';
					this.editConfirmButtonText = 'Edit Camp';
					this.editMessage = 'Click \'Edit Camp\' if you would like to add/remove camp days or lunch. You can also add/remove before or after care.<br/><br/>Please note, with the camp start date approaching, any removed days or lunches are non-refundable.';
				} else {
					this.editTitle = 'Modify Camp';
					this.editLinkButtonText = 'Edit Camp';
					this.editConfirmButtonText = 'Add to Camp';
					this.editMessage = 'Click \'Add to Camp\' below if you would like to add additional days or add-ons (such as lunch) to your camp reservation.<br/><br/>If you would like to remove days, before or after care, or lunches from your existing camp reservation, please choose \'Edit Camp\'.';
				}
		},

		buildCtaButtonText(cta: Cta) {
			return cta.text + ' Reservation';
		},

		buildPhoneCta(cta: Cta, clubPhone?: string) {
			if (cta.text && cta.text.indexOf('{0}') > -1 && clubPhone) {
				const phoneDisplay = Vue.filter('phoneFilter')(clubPhone, 'display');
				const phoneUrl = Vue.filter('phoneFilter')(clubPhone, 'telUrl');
				const phoneAria = Vue.filter('phoneFilter')(clubPhone, 'ariaLabel');
				const txt = `<a href="${phoneUrl}" :aria-label="${phoneAria}">${phoneDisplay}</a>`;
				return cta.text.replace('{0}', txt);
			}

			return cta.text;
		},

		isCamp(category: ReservationCategory) {
			return category === ReservationCategory.Camp;
		},

		isCancellationWindowOpen(reservation: Reservation) {
			if (reservation.cancelByDate) {
				const cancelByEndOfDay = moment(reservation.cancelByDate.split('T')[0]).endOf('day');
				return this.isCamp(reservation.category) && moment().isSameOrBefore(cancelByEndOfDay);
			}
			return false;
		},

		formatCancelByDate(cancelBy: any) {
			const cancelByDate = moment(cancelBy.split('T')[0]);
			return cancelByDate.format('MMM DD, YYYY');
		},

		handleFiltersChanged(updatedFilters: ReservationsFilters) {
			this.searchFilters = FilteringService.getUpdatedFilters(FilteringServicePages.myReservations, updatedFilters, this.filterTypes);
			this.searchChiclets = this.searchFilters.chiclets;
			this.isWaiting = true;
			this.loadMyReservations();
		},

		handleRemovedChiclet(chiclet: Chiclet) {
			(this.$refs.myReservationsFiltersSub as InstanceType<typeof SearchFilters>).deselectFilterValue(chiclet.type, chiclet.value);
		},

		handleClearAllChiclets() {
			for (const filterType of this.filterTypes) {
				if (filterType !== FilterTypes.startDate && filterType !== FilterTypes.endDate) {
					StateService.removeQueryStringKey(filterType);
				}
			}
			window.location.reload();
		},

		addToCalendar(reservation: Reservation) {
 			(this.$refs.addToCalendarComponent as InstanceType<typeof AddToCalendar>).open(
				reservation.eventId, // eventId
				reservation.id, // registrationId
				reservation.reservationType === 'Spa', // isSpaReservation
				reservation.start // reservationDate
			);
		},

		getEndDate(reservation: Reservation) {
			return reservation.groupingDetail && reservation.startMoment.format('YYYY-MM-DD') !== reservation.endMoment.format('YYYY-MM-DD') ? reservation.endMoment : undefined;
		},

		formatCampRange(start: any, end: any) {
			const startFormat = start.format('YYYY') === end.format('YYYY') ? 'ddd, MMM D' : 'ddd, MMM D, YYYY';
			const endFormat = 'ddd, MMM D, YYYY';
			return start.format(startFormat) + ' - ' + end.format(endFormat);
		}
	}
});
</script>

<style lang="scss" scoped>
@import "@/common/styles/panels.scss";
@import "@/common/styles/titleSection.scss";
@import "@/common/styles/loading.scss";
@import "styles/myReservations.scss";
</style>
